import { useMemo } from 'react';

import { type AutoSuggestOption } from '../components/AutoComplete/AutoComplete';

export type AutoSuggestOptionGroup = {
  items: AutoSuggestOption[];
  value: string;
  displayHeader?: boolean;
  isRecentSearches?: boolean;
};

type AutoSuggestOptionGroups = {
  recentSearches: AutoSuggestOptionGroup;
  apiSuggestions: AutoSuggestOptionGroup;
};

export const useAutoSuggestOptionGroups = (
  searchSavedAutosuggest: AutoSuggestOption[],
  savedAutosuggest: AutoSuggestOption[],
  items?: AutoSuggestOption[],
): AutoSuggestOptionGroups => {
  return useMemo(() => {
    const apiSuggestions = items ?? [];
    const hasApiAutosuggest = apiSuggestions.length > 0;
    const hasSavedSuggestions = searchSavedAutosuggest.length > 0;

    const getRecentSearches = (): AutoSuggestOption[] => {
      if (!hasSavedSuggestions && !hasApiAutosuggest) {
        // Show up to 5 recent searches when there are no suggestions
        return savedAutosuggest.slice(0, 5);
      }

      if (hasSavedSuggestions) {
        // Show up to 5 saved suggestions when there are no API suggestions
        return searchSavedAutosuggest.slice(0, 5);
      }

      return [];
    };

    const recentSearches = getRecentSearches();
    const filteredApiSuggestions =
      hasSavedSuggestions && hasApiAutosuggest
        ? apiSuggestions.filter((item: AutoSuggestOption) => !recentSearches.some((s) => s.id === item.id))
        : apiSuggestions;

    return {
      recentSearches: {
        items: recentSearches,
        value: 'saved_autosuggest',
        displayHeader: recentSearches.length > 0 && !hasApiAutosuggest,
        isRecentSearches: true,
      },
      apiSuggestions: {
        items: filteredApiSuggestions,
        value: 'autosuggest_api',
      },
    };
  }, [items, savedAutosuggest, searchSavedAutosuggest]);
};
